import { type ReactNode, useRef, useState } from "react";

import { CommandDialog } from "../../ui/command-dialog";
import { TextAreaField } from "../../ui/fields";
import { describeTeamError } from "../errors";

// The shell of every team and coverage command dialog (CHT-768). It adds what is special to this area on top of the generic `CommandDialog` of
// the kit (`src/ui/command-dialog`):
//   - the internal reason: every material change of access or cover needs one (1 to 500 characters, staff only, kept in the audit row);
//   - the mapping of a failure to what the kit shows (`describeTeamError`).
// The kit owns the rules every confirm dialog shares: nothing is sent before confirm, ONE Idempotency-Key for the dialog (a retry after a
// failure that may have run sends the SAME key and the SAME body, because the fields are locked), Escape and Cancel do nothing while the
// command runs, and a failure is shown inline. A stale screen or a refusal that cannot work again offers Close only, and the page behind has
// already read the latest state (`onReload`); a failure that may work on a second try says "Try again"; a refused field (422) keeps the form
// and gets a new key. Nothing in a message holds a name, an id or the reason.

const REASON_MAX = 500;

type TeamCommandBuild =
  /** The command to send, built from the fields of the dialog. `reason` is the checked reason; `key` is the Idempotency-Key of the dialog. */
  | Readonly<{ ok: true; send: (reason: string, key: string) => Promise<unknown> }>
  /** A field is invalid: the dialog has set its own error and the first invalid control takes focus. */
  | Readonly<{ ok: false }>;

type Props = Readonly<{
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  reasonLabel?: string;
  /** The extra fields of this command. */
  children?: ReactNode;
  /** Builds the command from the dialog's own fields. Called on submit, after the reason is checked. */
  build: () => TeamCommandBuild;
  /** The command succeeded: the page reads again and closes the dialog. */
  onDone: () => void;
  /** The screen is stale or refused: the page behind reads again (the dialog stays open to say why). */
  onReload: () => void;
  onClose: () => void;
}>;

export function TeamCommandDialog({
  title,
  description,
  confirmLabel,
  tone = "primary",
  reasonLabel = "Reason",
  children,
  build,
  onDone,
  onReload,
  onClose,
}: Props) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  // The command built by the last successful check, and the reason it was checked with; `mutate` sends them.
  const command = useRef<Extract<TeamCommandBuild, { ok: true }> | null>(null);
  const checkedReason = useRef("");

  function validate(): boolean {
    const trimmed = reason.trim();
    const missing =
      trimmed === ""
        ? "Write a reason."
        : trimmed.length > REASON_MAX
          ? `Use at most ${REASON_MAX} characters.`
          : undefined;
    setReasonError(missing);
    // `build()` runs even when the reason is missing, so every invalid field shows its error at once.
    const built = build();
    if (missing !== undefined || !built.ok) return false;
    command.current = built;
    checkedReason.current = trimmed;
    return true;
  }

  return (
    <CommandDialog<void>
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      tone={tone}
      validate={validate}
      mutate={async (key) => {
        try {
          await command.current?.send(checkedReason.current, key);
          return { ok: true, value: undefined };
        } catch (error) {
          const described = describeTeamError(error);
          return {
            ok: false,
            failure: {
              message: described.message,
              retryable: described.retryable,
              // An invalid answer (422) lets the person fix a field; the next body is another command, so the kit gives it another key.
              keepForm: described.kind === "invalid",
              reload: described.reload,
              tone:
                described.kind === "stale" || described.kind === "conflict" ? "conflict" : "error",
              kind: described.kind,
            },
          };
        }
      }}
      onFailureReload={onReload}
      onDone={onDone}
      onClose={onClose}
    >
      {children}
      <TextAreaField
        id="team-reason"
        label={reasonLabel}
        hint="Staff only. It goes into the audit trail. The customer never sees it."
        value={reason}
        rows={3}
        maxLength={REASON_MAX}
        required
        error={reasonError}
        onChange={setReason}
      />
    </CommandDialog>
  );
}
